'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallbackUrl?: string;
}

export function ProtectedRoute({
  children,
  fallbackUrl = '/login'
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const router = useRouter();
  const { toast } = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    const verifyAuthentication = async () => {
      // If already loading, wait for that to complete
      if (isLoading) return;

      // If authenticated, allow access
      if (isAuthenticated) {
        setVerified(true);
        return;
      }

      // If not authenticated, redirect
      if (!isRefreshing && !isAuthenticated) {
        toast({
          variant: "destructive",
          title: "Требуется вход",
          description: "Войдите в аккаунт, чтобы открыть эту страницу"
        });
        router.push(fallbackUrl);
      }
    };

    verifyAuthentication();
  }, [isLoading, isAuthenticated, router, fallbackUrl, toast, isRefreshing]);

  // Show nothing while we're checking authentication
  if (isLoading || isRefreshing || !verified) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin h-10 w-10 border-4 border-blue-600 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  return <>{children}</>;
}
